import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { GlobeIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

function mapLocaleToLangCode(localeCode: string): string {
  const lower = localeCode.toLowerCase();
  if (["pt-br", "pt-pt", "zh-hans", "zh-hant"].includes(lower)) {
    return lower;
  }
  return lower.split("-")[0];
}

function getDisplayName(localeCode: string): string {
  try {
    const names = new Intl.DisplayNames([localeCode, "en"], { type: "language" });
    const langCode = localeCode.split("-")[0];
    const name = names.of(langCode);
    if (name) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    // fall through
  }
  return localeCode;
}

export function LanguageSelector() {
  const { shopId } = useParams<{ shopId: string }>();
  const { data: shop } = usePublicShop(shopId!);
  const { i18n } = useTranslation("return-widget-v3");

  const languages = shop?.preferences?.languages ?? [];

  if (languages.length <= 1) return null;

  const currentLangCode = i18n.language ?? "en";
  const currentLocale =
    languages.find((l) => mapLocaleToLangCode(l) === currentLangCode) ??
    languages[0];

  const handleChange = (localeCode: string) => {
    i18n.changeLanguage(mapLocaleToLangCode(localeCode));
  };

  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <GlobeIcon className="size-3.5 shrink-0" />
      <Select value={currentLocale} onValueChange={handleChange}>
        <SelectTrigger className="border-none shadow-none h-auto py-0 px-0 text-xs focus:ring-0 w-auto gap-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {languages.map((locale) => (
            <SelectItem key={locale} value={locale} className="text-xs">
              {getDisplayName(locale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
